import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
  isOnFoldersView,
  isOnWlabAiAgentView,
  getWlabAiAgentViewType,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  handleVoiceCallCreated,
  handleVoiceCallUpdated,
} from 'dashboard/helper/voice';

const conversationSendQueues = new Map();

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from WLAB but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

export const resetConversationSendQueues = () => {
  conversationSendQueues.clear();
};

// Saved messages carry the server's numeric id; a reply that is still being
// sent carries the string echo id createPendingMessage gave it.
export const isPersistedMessageId = id =>
  typeof id === 'number' && Number.isInteger(id) && id > 0;

const findChat = (state, conversationId) =>
  state.allConversations.find(
    conversation => conversation.id === conversationId
  );

// The realtime event for a reply replaces its pending bubble with the saved
// copy, keeping the echo id. When only the HTTP response was lost, that copy is
// already on screen by the time the request fails.
const hasSavedCopyOfPendingMessage = (state, conversationId, echoId) =>
  !!findChat(state, conversationId)?.messages?.some(
    message => message.echo_id === echoId && isPersistedMessageId(message.id)
  );

export const enqueueConversationSend = (conversationId, task) => {
  if (!conversationId) {
    return task();
  }

  const queueKey = String(conversationId);
  const previousTask =
    conversationSendQueues.get(queueKey) || Promise.resolve();
  const nextTask = previousTask.catch(() => undefined).then(task);
  const trackedTask = nextTask.catch(() => undefined);

  conversationSendQueues.set(queueKey, trackedTask);
  trackedTask.finally(() => {
    if (conversationSendQueues.get(queueKey) === trackedTask) {
      conversationSendQueues.delete(queueKey);
    }
  });

  return nextTask;
};

// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  // `silent: true` is passed by ReconnectService's background resyncs (the
  // 30s staleness watchdog, websocket reconnect, foreground return). They
  // refresh the list in place and must not raise listLoadingStatus: that flag
  // drives the full-pane "Loading Conversations" state, and on a low-traffic
  // account the watchdog fires every 60-90s for as long as the tab is open,
  // so agents saw the spinner cycling forever (ILoanu, 2026-09-21).
  fetchAllConversations: async (
    { commit, state, dispatch },
    { silent = false } = {}
  ) => {
    if (!silent) commit(types.SET_LIST_LOADING_STATUS);
    try {
      const params = state.conversationFilters;
      const {
        data: { data },
      } = await ConversationApi.get({
        ...params,
        includeMeta: false,
      });
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType
      );
      dispatch('conversationStats/get', params, { root: true });
    } catch (error) {
      // Handle error - clear loading status on failure (only if we raised it)
      if (!silent) commit(types.CLEAR_LIST_LOADING_STATUS);
      console.error('Error fetching conversations:', error);
      // Optionally show user-friendly error message
      if (error.response?.status === 404) {
        console.error('Conversations endpoint not found');
      } else if (error.response?.status === 500) {
        console.error('Server error while fetching conversations');
      }
    }
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    // Same `silent` contract as fetchAllConversations, for a background resync
    // of a saved folder / applied filter. ConversationApi.filter only reads
    // queryData/page, so the extra key never reaches the request.
    const { silent = false } = params || {};
    if (!silent) commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } catch (error) {
      // Handle error - clear loading status on failure (only if we raised it)
      if (!silent) commit(types.CLEAR_LIST_LOADING_STATUS);
      console.error('Error fetching filtered conversations:', error);
      // Optionally show user-friendly error message
      if (error.response?.status === 404) {
        console.error('Filter endpoint not found');
      } else if (error.response?.status === 500) {
        console.error('Server error while fetching filtered conversations');
      }
      // Always mark end reached on error to prevent infinite loading
      dispatch(
        'conversationPage/setEndReached',
        { filter: 'appliedFilters' },
        { root: true }
      );
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      if (data.replace) {
        commit(types.SET_MISSING_MESSAGES, {
          id: data.conversationId,
          data: payload,
        });
      } else {
        commit(types.SET_PREVIOUS_CONVERSATIONS, {
          id: data.conversationId,
          data: payload,
        });
      }
      if (!payload.length || data.replace) {
        commit(types.SET_ALL_MESSAGES_LOADED);
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = [];

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  // `reconcile: true` is ReconnectService's periodic check of the open chat
  // while the stream still looks healthy. It runs every minute for every
  // agent, so it re-reads the latest page, leaves the reconnect cursor alone,
  // and changes nothing unless a message is actually missing.
  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId, reconcile = false }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = reconcile
      ? undefined
      : syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    // A reply still being sent has no saved id, so the fetched copy of it would
    // land next to its pending bubble. The send settles that bubble itself.
    const isSending = (selectedChat.messages || []).some(
      message => message.status === MESSAGE_STATUS.PROGRESS
    );
    if (reconcile && isSending) return;
    try {
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      // Find the messages that are not already present in the store. This has
      // to read the array after the await: the fetch above gives realtime
      // events and SET_MISSING_MESSAGES time to replace it, and deduping
      // against a pre-await snapshot re-appends messages that are already
      // there.
      const known = new Set((selectedChat.messages || []).map(m => m.id));
      const missingMessages = payload.filter(message => !known.has(message.id));
      if (reconcile && !missingMessages.length) return;
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        const normalize = v => {
          if (v == null) return 0;
          if (typeof v === 'number') return v;
          const n = Number(v);
          if (!Number.isNaN(n) && Number.isFinite(n)) return n;
          const ms = Date.parse(String(v));
          return Number.isFinite(ms) ? Math.floor(ms / 1000) : 0;
        };
        return normalize(a.created_at) - normalize(b.created_at);
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      if (!reconcile) {
        commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
          conversationId,
          messageId: null,
        });
      }
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    // The resync cursor must be a saved message id. A reply still being sent
    // only has its client-side echo id, which the server read as a number
    // ("58df9a3946b9" -> 58), so the resync fetched the oldest page instead of
    // what arrived while the socket was down.
    const lastPersistedMessage = [...selectedChat.messages]
      .reverse()
      .find(message => isPersistedMessageId(message.id));
    if (!lastPersistedMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastPersistedMessage.id,
    });
  },

  async setActiveChat(
    { commit, dispatch, state },
    { data, after, windowFrom, windowTo, forceReload }
  ) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED);
    const useSessionWindow = Boolean(windowFrom || windowTo);
    const shouldReplace = useSessionWindow || Boolean(forceReload);
    if (shouldReplace) {
      commit(types.SET_MISSING_MESSAGES, { id: data.id, data: [] });
      data.dataFetched = undefined;
    }
    if (data.dataFetched === undefined) {
      // Session window / full reload: load a fresh page (no before=cursor).
      // Normal chat: page backward from the first already-loaded message.
      const beforeMessageId = shouldReplace
        ? undefined
        : data?.messages?.[0]?.id;
      try {
        await dispatch('fetchPreviousMessages', {
          after,
          before: beforeMessageId,
          conversationId: data.id,
          windowFrom,
          windowTo,
          replace: shouldReplace,
        });
      } catch (error) {
        // Ignore error
      } finally {
        // Avoid an infinite "loading" state if the first fetch fails.
        data.dataFetched = true;
        const stored = state.allConversations.find(c => c.id === data.id);
        if (stored) stored.dataFetched = true;
      }
    }
  },

  assignAgent: async ({ dispatch }, { conversationId, agentId }) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
      });
      dispatch('setCurrentChatAssignee', response.data);
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatAssignee({ commit }, assignee) {
    commit(types.ASSIGN_AGENT, assignee);
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  assignIssue: async ({ dispatch }, { conversationId, issueId }) => {
    console.log('[conversations/actions] assignIssue called:', {
      conversationId,
      issueId,
    });
    try {
      const response = await ConversationApi.assignIssue({
        conversationId,
        issueId,
      });
      console.log(
        '[conversations/actions] assignIssue API response:',
        response.data
      );
      dispatch('setCurrentChatIssue', { issue: response.data, conversationId });
      return response;
    } catch (error) {
      console.error('[conversations/actions] assignIssue error:', error);
      // Handle error
      throw error;
    }
  },

  setCurrentChatIssue({ commit }, { issue, conversationId }) {
    console.log('[conversations/actions] setCurrentChatIssue called:', {
      issue,
      conversationId,
    });
    commit(types.ASSIGN_ISSUE, { issue, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null, caseCloseType = null }
  ) => {
    try {
      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
        caseCloseType,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    return dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit, dispatch, state }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    console.log('[Message] Sending message:', pendingMessage);
    // Optimistically add the pending message immediately so the UI stays instant
    // even though the actual API send is serialized per conversation.
    try {
      dispatch('updateConversationLastActivity', {
        conversationId,
        lastActivityAt: Math.floor(Date.now() / 1000),
      });
    } catch (e) {
      // noop
    }

    commit(types.ADD_MESSAGE, {
      ...pendingMessage,
      status: MESSAGE_STATUS.PROGRESS,
    });

    return enqueueConversationSend(conversationId, async () => {
      try {
        const response = hasMessageFailedWithExternalError(pendingMessage)
          ? await MessageApi.retry(conversationId, id)
          : await MessageApi.create(pendingMessage);
        console.log('[Message] Message sent successfully:', response.data);
        commit(types.ADD_MESSAGE, {
          ...response.data,
          status: MESSAGE_STATUS.SENT,
        });
        commit(types.ADD_CONVERSATION_ATTACHMENTS, {
          ...response.data,
          status: MESSAGE_STATUS.SENT,
        });

        // Use server-provided last_activity_at when available (authoritative).
        try {
          const serverLast =
            Number(response?.data?.conversation?.last_activity_at) ||
            Number(response?.data?.created_at);
          if (serverLast) {
            dispatch('updateConversationLastActivity', {
              conversationId,
              lastActivityAt: serverLast,
            });
          }
        } catch (e) {
          // noop
        }
      } catch (error) {
        // Marking it failed here would add a second, "failed" bubble next to a
        // reply the customer already received, and invite a duplicate resend.
        if (hasSavedCopyOfPendingMessage(state, conversationId, id)) {
          console.warn('[Message] Send response lost; message was saved:', id);
          return;
        }
        console.error('[Message] Failed to send message:', error);
        const errorMessage = error.response
          ? error.response.data.error
          : undefined;
        commit(types.ADD_MESSAGE, {
          ...pendingMessage,
          meta: {
            error: errorMessage,
          },
          status: MESSAGE_STATUS.FAILED,
        });
        throw error;
      }
    });
  },

  addMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
    handleVoiceCallCreated(message, rootGetters?.getCurrentUserID);
  },

  updateMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    handleVoiceCallUpdated(commit, message, rootGetters?.getCurrentUserID);
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteConversation: async ({ commit, dispatch }, conversationId) => {
    try {
      await ConversationApi.delete(conversationId);
      commit(types.DELETE_CONVERSATION, conversationId);
      dispatch('conversationStats/get', {}, { root: true });
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
      status: conversationStatus,
      is_ai_agent_inbox: isAiAgentInbox,
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;

    // A Wlab AI Agent view is scoped server-side by ConversationFinder, so a
    // websocket conversation that does not belong to the view must not be pushed
    // into the list either — otherwise realtime and refresh disagree.
    if (isOnWlabAiAgentView(rootState)) {
      if (!isAiAgentInbox) {
        return;
      }
      const viewType = getWlabAiAgentViewType(rootState);
      if (viewType === 'pending' && conversationStatus !== 'pending') {
        return;
      }
      if (viewType === 'resolved' && conversationStatus !== 'resolved') {
        return;
      }
      // all / handoff tabs accept any status.
    }

    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch, rootState, state }, conversation) {
    const {
      meta: { sender },
      status: conversationStatus,
      is_ai_agent_inbox: isAiAgentInbox,
    } = conversation;

    // On a Wlab AI Agent view a conversation stops belonging to the list once it
    // is not from an AI Agent inbox or its status no longer fits the active tab.
    // Evict it instead of updating in place, and if it is the open one, tell the
    // view to go back to the list so a stale selection cannot request
    // /contacts/undefined.
    if (isOnWlabAiAgentView(rootState)) {
      const viewType = getWlabAiAgentViewType(rootState);
      const matchesView =
        !!isAiAgentInbox &&
        !(viewType === 'pending' && conversationStatus !== 'pending') &&
        !(viewType === 'resolved' && conversationStatus !== 'resolved');

      if (!matchesView) {
        const existsInList = state.allConversations.some(
          c => c.id === conversation.id
        );
        if (existsInList) {
          const isCurrentlyOpen = state.selectedChatId === conversation.id;
          commit(types.DELETE_CONVERSATION, conversation.id);
          if (isCurrentlyOpen) {
            emitter.emit(
              BUS_EVENTS.CURRENT_CONVERSATION_REMOVED,
              conversation.id
            );
          }
        }
        return;
      }
    }

    commit(types.UPDATE_CONVERSATION, conversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    try {
      await ConversationApi.sendEmailTranscript({ conversationId, email });
    } catch (error) {
      throw new Error(error);
    }
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, custom_attributes);
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  getInboxAilabAssistantById: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.getInboxAssistant(conversationId);
      commit(types.SET_INBOX_AILAB_ASSISTANT, response.data);
    } catch (error) {
      // Handle error
    }
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
